import type { InfiniteData } from "@tanstack/react-query";
import axios from "axios";

import type {
	Chat,
	Message,
	MessageWithContents,
	PromptCategory,
	WebMode,
} from "@virtual-brain-lib/types";

import env from "../environments";
import { infiniteQueryOptions, queryOptions } from "../query-client";

export const CHATS_PER_PAGE = 40;

type ChatsPage = { chats: Chat[]; countTotal: number };
export type ChatsInfiniteData = InfiniteData<ChatsPage>;

export async function createChat({
	title,
	welcome,
}: {
	title: string;
	welcome?: boolean | undefined;
}) {
	const response = await axios.post<Chat>(`${env.apiUrl}/chats/create`, {
		title,
		welcome,
	});

	return response.data;
}

export async function getChatMessages(
	id: string,
	limit?: number | undefined,
	offset?: number | undefined,
) {
	const response = await axios.get<MessageWithContents[]>(`${env.apiUrl}/chats/${id}/messages`, {
		params: {
			offset,
			limit,
		},
	});

	return response.data;
}


export const getAllChats = infiniteQueryOptions({
	async queryFn({ queryKey, pageParam }) {
		const [, params] = queryKey;
		const response = await axios.get<ChatsPage>(`${env.apiUrl}/chats`, {
			params: { ...params, limit: CHATS_PER_PAGE, offset: pageParam },
		});

		return response.data;
	},
	queryKey: "allChats",
	initialPageParam: 0,
	getNextPageParam(lastPage, allPages) {
		const loadedCount = allPages.reduce((total, page) => total + (page.chats?.length ?? 0), 0);

		return loadedCount < lastPage.countTotal ? loadedCount : undefined;
	},
});

export const getChatById = queryOptions({
	async queryFn({ queryKey }) {
		const [, { id }] = queryKey;
		const response = await axios.get<Chat>(`${env.apiUrl}/chats/${id}`);

		return response.data;
	},
	queryKey: "chat",
});

export async function updateChatTitle({ id, title }: { id: string; title: string }) {
	await axios.put(`${env.apiUrl}/chats/${id}`, {
		title,
	});
}

export async function deleteChat(id: string) {
	await axios.delete(`${env.apiUrl}/chats/${id}`);
}

export async function duplicateChat(id: string) {
	const response = await axios.post<Chat>(`${env.apiUrl}/chats/${id}/duplicate`);

	return response.data;
}

export async function sendChatMessage(
	chatId: string,
	query: string,
	brainIds: string[],
	contentIds: string[],
	options?: {
		webMode?: WebMode;
		openedArtifactId?: string;
	},
) {
	const endpoint = `${env.apiUrl}/chats/${chatId}/completion`;

	const response = await axios.post<{ userMessage: Message; assistantMessage: Message }>(
		endpoint,
		{ query, brainIds, contentIds, ...options },
	);

	return response.data;
}

export async function rephrase({ query, chatId }: { query: string; chatId: string }) {
	const response = await axios.post<string>(`${env.apiUrl}/chats/${chatId}/rephrase`, {
		query,
	});

	return response.data;
}

export const getChatPromptsCategories = queryOptions({
	async queryFn() {
		const response = await axios.get<PromptCategory[]>(`${env.apiUrl}/chats/prompts`);

		return response.data;
	},
	queryKey: "chatPromptCategories",
});

export async function sendExhaustiveMessage(chatId: string, brainsIds: string[], query: string) {
	const endpoint = `${env.apiUrl}/chats/${chatId}/completion_exhaustive`;

	const payload = {
		query,
		brainsIds,
	};

	const response = await axios.post<{ userMessage: Message; assistantMessage: Message }>(
		endpoint,
		payload,
	);

	return response.data;
}
